import { useState, useContext, useCallback, memo, useEffect } from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import NumericKeypad from "../../../common/NumericKeypad";
import {
  createSaleOrderLine,
  ObserveDraftSaleOrderByBusiness,
  deleteSaleOrderById,
  updateSaleOrderStatusById,
  getSaleOrderByID,
} from "./../../../../database/helpers/sale_order";
import {
  ObserveSaleOrderLineBySaleOrder,
  updateSaleOrderLine,
  deleteAllSaleOrderLineById,
} from "./../../../../database/helpers/sale_order_line";
import withObservables from "@nozbe/with-observables";
import { LanguageContext } from "../../../../context/LanguageContext";
import StockItemList from "./../StockItemList";
import { OrderDraftListModal } from "..";
import { ACTIVE, DRAFT } from "./../../../../constants";
import styles from "./styles.module.css";

import { analytics, triggerLogEvent } from "../../../../config/firebase";
import ConfirmationModal from "../../../common/Modals/ConfirmationModal";

const SaleOrder = memo(
  ({
    activeSaleOrder,
    sale_order_line,
    business,
    setActiveSaleOrder = () => { },
    navigate,
    user,
    draftListModal,
    setDraftListModal,
  }) => {
    const [value, setValue] = useState(1);
    const [isShowQuickStockForm, setIsShowQuickStockForm] = useState(false);
    const [onGoingSaleModal, setOnGoingSaleModal] = useState(false);
    const [resumeSaleOrder, setResumeSaleOrder] = useState(null);
    const [selectedItem, setSelectedItem] = useState();
    const [I18n] = useContext(LanguageContext);

    const handleChange = useCallback((event, newValue) => {
      triggerLogEvent(
        analytics,
        newValue === 0 ? "pos_stock_item_selected" : "pos_open_item_selected"
      );

      setValue(newValue);
    }, []);

    const createOrderOpenItem = useCallback(
      async (itemAmount) => {
        try {
          const itemObj = {
            name: `Open item #${sale_order_line.length + 1}`,
            selling_price: Number(itemAmount),
            discount_method: "rs",
            is_open_item: true,
            unit: "Pieces",
            discount: null,
            quantity: 1,
            note: "",
          };
          await createSaleOrderLine(itemObj, activeSaleOrder);
        } catch (error) {
        }
      },
      [sale_order_line, activeSaleOrder]
    );

    const isStockExistInOrderLine = useCallback(
      (stockId) => {
        return (sale_order_line ?? []).find(
          (item) => item._raw.stock_id === stockId
        );
      },
      [sale_order_line]
    );

    const createOrderStockItem = useCallback(
      async (item) => {
        try {
          const saleOrderItem = isStockExistInOrderLine(item.id);
          if (saleOrderItem) {
            await saleOrderItem.updateQuantity(saleOrderItem.quantity + 1);
            return;
          }
          const itemObj = {
            name: item._raw.name,
            selling_price: item._raw.selling_price,
            cost_price: item._raw.cost_price,
            discount_method: "rs",
            unit: item._raw.unit,
            discount: null,
            quantity: 1,
            id: item._raw.id,
          };
          await createSaleOrderLine(itemObj, activeSaleOrder);
        } catch (error) {
        }
      },
      [isStockExistInOrderLine, activeSaleOrder]
    );

    const onChangeNoteText = useCallback(
      async (note) => {
        try {
          if (!Array.isArray(activeSaleOrder)) {
            await activeSaleOrder.updateSaleOrderNote(note);

          }
        } catch (error) {
        }
      },
      [activeSaleOrder]
    );

    const onDeleteDraftOrderSale = useCallback(async () => {
      if (!selectedItem) return;
      try {
        await deleteAllSaleOrderLineById(selectedItem._raw.id);
        await deleteSaleOrderById(selectedItem._raw.id);
        setSelectedItem();
        triggerLogEvent(analytics, "pos_draft_delete");
      } catch (error) {
      }
    }, [selectedItem]);

    const onResumeDraftOrderSale = useCallback(
      async (saleOrder) => {
        try {
          setDraftListModal(false);
          if (sale_order_line.length) {
            setResumeSaleOrder(saleOrder);
            setOnGoingSaleModal(true);
            return;
          }
          await deleteSaleOrderById(activeSaleOrder?.id);
          await updateSaleOrderStatusById(saleOrder._raw.id, ACTIVE);
          triggerLogEvent(analytics, "pos_draft_resume");

          setOnGoingSaleModal(false);
        } catch (error) {
        }
      },
      [sale_order_line, activeSaleOrder, setActiveSaleOrder]
    );

    const discardOrder = useCallback(async () => {
      try {
        await deleteAllSaleOrderLineById(activeSaleOrder?.id);
        await deleteSaleOrderById(activeSaleOrder?.id);
        await updateSaleOrderStatusById(resumeSaleOrder._raw.id, ACTIVE);
        triggerLogEvent(analytics, "pos_draft_sale_resume");
        setOnGoingSaleModal(false);
      } catch (error) {
      }
    }, [activeSaleOrder, resumeSaleOrder, setActiveSaleOrder]);

    const saveCurrentOrderInDraft = useCallback(async () => {
      try {
        await activeSaleOrder.updateSaleOrderStatus(DRAFT);
        await updateSaleOrderStatusById(resumeSaleOrder._raw.id, ACTIVE);
        triggerLogEvent(analytics, "pos_draft_sale_resume");
        setOnGoingSaleModal(false);
      } catch (error) {
      }
    }, [activeSaleOrder, resumeSaleOrder, setActiveSaleOrder]);

    const handleOngoingSaleModalClose = (event, reason) => {
      if (reason === "backdropClick" || reason === "escapeKey") {
        setOnGoingSaleModal(false);
        return;
      }
      discardOrder();
    };

    useEffect(() => {
      const handleKeyDown = (event) => {
        const activeElement = document.activeElement;
        const tagName = activeElement.tagName.toLowerCase();
        const className = activeElement.className;
        const isContentEditable = activeElement.isContentEditable;
        if (
          (tagName === "input" ||
            tagName === "textarea" ||
            isContentEditable) &&
          !className.includes("sale-rupees-input")
        ) {
          return;
        }
        if (event.code === "Space") {
          event.preventDefault();
          setValue(value === 0 ? 1 : 0);
        }
      };

      window.addEventListener("keydown", handleKeyDown);

      // Cleanup on unmount
      return () => {
        window.removeEventListener("keydown", handleKeyDown);
      };
    }, [value]);

    const TabPanel = useCallback(({ children, value, index }) => {
      return (
        <div
          role="tabpanel"
          hidden={value !== index}
          id={`vertical-tabpanel-${index}`}
          aria-labelledby={`vertical-tab-${index}`}
          className={styles.tabPanelRoot}
        >
          {value === index && (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                boxSizing: "border-box",
              }}
            >
              {children}
            </Box>
          )}
        </div>
      );
    }, []);

    return (
      <Box className={styles.saleOrderContainer}>
        <div className={styles.saleOrderBody}>
          <div className={styles.saleOrderBodyInner}>
            <Tabs
              value={value}
              onChange={handleChange}
              textColor="primary"
              indicatorColor="primary"
              aria-label="primary tabs example"
              variant="fullWidth"
            >
              <Tab
                id="stock_item"
                value={0}
                label={I18n.stock_list}
                sx={{ textTransform: "none" }}
              />
              <Tab
                id="open_item"
                value={1}
                label={I18n.open_item}
                sx={{ textTransform: "none" }}
              />
            </Tabs>
          </div>
          <Box
            className={`${styles.saleOrderTabsSection} generic-scrollbar ${
              value === 0 ? "" : ""
            }`}
          >
            <TabPanel value={value} index={0}>
              <StockItemList
                onClickStockItem={createOrderStockItem}
                business={business}
                isShowQuickStockForm={isShowQuickStockForm}
                setIsShowQuickStockForm={setIsShowQuickStockForm}
                activeSaleOrder={activeSaleOrder}
                setValue={setValue}
                navigate={navigate}
                user={user}
              />
            </TabPanel>
            <TabPanel value={value} index={1}>
              <NumericKeypad
                onSubmit={createOrderOpenItem}
                onChangeNoteText={onChangeNoteText}
                noteText={activeSaleOrder?.note ?? ""}
                handleOpenItemLogEvent={() =>
                  triggerLogEvent(analytics, "pos_open_start")
                }
                handleNoteLogEvent={() => triggerLogEvent(analytics, "pos_note_start")}
              />
            </TabPanel>
          </Box>
        </div>
        <OrderDraftListModal
          isOpen={draftListModal}
          handleClose={() => setDraftListModal(false)}
          onDelete={(item) => {
            setSelectedItem(item);
            setDraftListModal(false);
          }}
          onResume={onResumeDraftOrderSale}
          business={business}
        />
        <ConfirmationModal
          id="sale_pos"
          open={Boolean(selectedItem)}
          onClose={() => setSelectedItem()}
          message={I18n.delete_draft_desc}
          title={I18n.this_action_cannot_be_undone}
          cancelText={I18n.cancel}
          confirmText={I18n.delete}
          onConfirm={onDeleteDraftOrderSale}
        />
        <ConfirmationModal
          id="sale_pos_on_going_sale"
          open={onGoingSaleModal}
          onClose={handleOngoingSaleModalClose}
          message={I18n.on_going_sale_desc}
          title={I18n.this_action_cannot_be_undone}
          cancelText={I18n.discard}
          confirmText={I18n.confirm}
          confirmButtonClassName="ubPrimaryButton"
          onConfirm={saveCurrentOrderInDraft}
        />
      </Box>
    );
  }
);

const enhance = withObservables(
  ["activeSaleOrder", "sale_order_line"],
  ({ activeSaleOrder }) => ({
    sale_order_line: ObserveSaleOrderLineBySaleOrder(activeSaleOrder?.id ?? ""),
  })
);

export default enhance(SaleOrder);
